import React, { useEffect } from 'react'
import { usePanelResize } from './usePanelResize'
import {
  ResizableDetailPanelProps,
  DetailPanelHeaderProps,
  DetailPanelContentProps,
  DetailPanelFooterProps,
} from './ResizableDetailPanel.types'
import {
  StyledDetailPanel,
  StyledDetailPanelHeader,
  StyledDetailPanelContent,
  StyledDetailPanelFooter,
  ResizeHandle,
  CloseButton,
} from './ResizableDetailPanel.styles'

export const ResizableDetailPanel: React.FC<ResizableDetailPanelProps> = ({
  isOpen,
  onClose,
  children,
  storageKey,
  defaultWidth = '800px',
  minWidth = 400,
  maxWidthPercentage = 0.8,
  contained = false,
  disableResize = false,
}) => {
  const { panelWidth, isResizing, handleResizeStart } = usePanelResize({
    storageKey,
    defaultWidth,
    minWidth,
    maxWidthPercentage,
  })

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''
    }
  }, [isOpen, onClose])

  // While the fixed (viewport-overlay) panel is open, publish its live width as a CSS variable so
  // overlaid map controls can offset themselves to the left of it (via calc()) instead of being
  // hidden underneath — letting the user change basemap / 3D / point cloud while a plot is selected.
  // The `contained` (in-container) and `disableResize` (full-width) variants don't overlay the map,
  // so they don't publish. Tracks resize live (panelWidth updates) and clears on close/unmount.
  useEffect(() => {
    if (contained || disableResize) return
    const root = document.documentElement
    if (isOpen)
      root.style.setProperty('--popety-detail-panel-width', panelWidth)
    else root.style.removeProperty('--popety-detail-panel-width')
    return () => root.style.removeProperty('--popety-detail-panel-width')
  }, [isOpen, panelWidth, contained, disableResize])

  useEffect(() => {
    if (contained || disableResize) return
    const root = document.documentElement
    root.classList.toggle('popety-panel-resizing', isResizing)
    return () => root.classList.remove('popety-panel-resizing')
  }, [isResizing, contained, disableResize])

  return (
    <StyledDetailPanel
      $isOpen={isOpen}
      $width={panelWidth}
      $isResizing={isResizing}
      $contained={contained}
      $disableResize={disableResize}
      data-resizable-panel
    >
      {!disableResize && <ResizeHandle onMouseDown={handleResizeStart} />}
      {children}
    </StyledDetailPanel>
  )
}

export const DetailPanelHeader: React.FC<DetailPanelHeaderProps> = ({
  children,
  onClose,
  className,
}) => {
  return (
    <StyledDetailPanelHeader className={className}>
      {onClose && (
        <CloseButton onClick={onClose} aria-label="Close panel">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
        </CloseButton>
      )}
      {children}
    </StyledDetailPanelHeader>
  )
}

export const DetailPanelContent = React.forwardRef<
  HTMLDivElement,
  DetailPanelContentProps
>(({ children, className }, ref) => {
  return (
    <StyledDetailPanelContent ref={ref} className={className}>
      {children}
    </StyledDetailPanelContent>
  )
})

DetailPanelContent.displayName = 'DetailPanelContent'

export const DetailPanelFooter: React.FC<DetailPanelFooterProps> = ({
  children,
  className,
}) => {
  return (
    <StyledDetailPanelFooter className={className}>
      {children}
    </StyledDetailPanelFooter>
  )
}
